/* ---------- Cart drawer: "You may also like" panel ----------
   Two independent jobs, both re-run on every cart change since the drawer
   content is fully replaced by Shopify's Section Rendering API each time
   (same pattern as assets/cart-shipping-bar.js):

   1. Mobile panel toggle -- the data-cart-panel-tab buttons (rendered in
      snippets/cart-drawer.liquid and snippets/cart-recommendations.liquid)
      slide #CartDrawerPanels between the bag and the recommendations panel
      by toggling a class (see assets/component-cart-drawer.css). Bound via
      delegation on `document` since the buttons themselves get replaced on
      every cart update.

   2. Native recommendation top-up -- snippets/cart-recommendations.liquid
      can only rank products from cart items' weltonlondon.you_may_like metafields,
      which may add up to fewer than 4. When that happens it renders a
      hidden #CartDrawerRecommendationsFillTrigger with the data this script
      needs to fetch Shopify's own recommendation engine (seeded from the
      first cart item) and top up the grid to 4, the same
      routes.product_recommendations_url + section_id pattern Dawn's own
      product-recommendations feature uses (see assets/global.js
      ProductRecommendations) -- reusing section_id=cart-drawer so the
      request re-renders this exact snippet with `recommendations` populated,
      then this script extracts just the #CartDrawerRecommendationsFillTarget
      fragment from that response and discards the rest.

      Tries intent=complementary first (best fit for a cart context -- "goes
      well with what's already in the bag"), but that intent is powered by
      real order history, which a lower-volume/dev store may not have yet
      for a given product -- confirmed via manual testing that Shopify
      returns recommendations.performed = true, products_count = 0 in that
      case (not an error, just no data). When that happens, retries once
      with Shopify's default "related" intent (tags/type/collection based,
      no order history required), which reliably has something to show.

      Trigger timing: subscribing to PUB_SUB_EVENTS.cartUpdate alone isn't
      enough to catch "add to cart" specifically -- product-form.js
      publishes that event BEFORE calling cart-drawer.js's
      renderContents(), which is what actually swaps in the drawer's new
      HTML (including a fresh #CartDrawerRecommendationsFillTrigger). Firing
      on cartUpdate alone means this runs against the *old*, about-to-be-
      replaced DOM, finds nothing useful, and never runs again until the
      next cart change or a full page reload -- exactly the "works after
      reload but not right after adding to cart" symptom. A MutationObserver
      on #CartDrawer's subtree catches the actual DOM swap regardless of
      which code path/timing triggered it, so that's the primary signal;
      cartUpdate stays subscribed too as a harmless backup.

      Re-entrancy: appendChild-ing candidates into the grid is itself a
      childList mutation, so the observer above sees its own writes and
      would otherwise call fillRecommendations() again forever, re-fetching
      and re-appending another batch each time (trigger.dataset.remaining
      never changes) -- a runaway loop that visibly duplicated the same 4
      products dozens of times. A boolean flag reset synchronously right
      after the appendChild loop does NOT fix this: MutationObserver
      callbacks run as a microtask *after* the current synchronous stack
      finishes, i.e. after the flag's already back to false. So instead the
      trigger element itself is marked filled (data-filled="true")
      synchronously, before the async fetch even starts -- any re-entrant
      call (from the observer noticing our own DOM writes, or from
      cartUpdate/DOMContentLoaded firing around the same time) sees the
      marker on that same trigger instance and bails immediately. A
      server-rendered replacement trigger (next cart change) never carries
      this marker, so it's naturally re-fillable. */

(function () {

  document.addEventListener('click', function (event) {
    const tab = event.target.closest('[data-cart-panel-tab]');
    if (!tab) return;

    const panels = document.getElementById('CartDrawerPanels');
    if (!panels) return;

    panels.classList.toggle('show-recommendations', tab.dataset.cartPanelTab === 'recommendations');

    const recommendations = document.querySelector('.cart-drawer__recommendations');
    const bag = document.querySelector('.cart-drawer__bag');
    if (recommendations && bag) {
      const showingRecommendations = panels.classList.contains('show-recommendations');
      recommendations.setAttribute('aria-hidden', String(!showingRecommendations));
      bag.setAttribute('aria-hidden', String(showingRecommendations));
    }
  });

  function fillRecommendations() {
    const trigger = document.getElementById('CartDrawerRecommendationsFillTrigger');
    const grid = document.getElementById('CartDrawerRecommendationsGrid');
    if (!trigger || !grid) return;

    // Bail if this exact trigger instance has already been (or is being)
    // filled -- see the re-entrancy note above. Set the marker immediately,
    // synchronously, so any re-entrant call arriving before the fetch below
    // resolves also bails.
    if (trigger.dataset.filled === 'true') return;

    const remaining = parseInt(trigger.dataset.remaining, 10) || 0;
    if (remaining <= 0) return;

    trigger.dataset.filled = 'true';

    const excludeIds = (trigger.dataset.exclude || '')
      .split(',')
      .map(function (id) {
        return id.trim();
      })
      .filter(Boolean);

    const baseUrl =
      trigger.dataset.url +
      '?product_id=' +
      encodeURIComponent(trigger.dataset.productId) +
      '&limit=10&section_id=' +
      encodeURIComponent(trigger.dataset.sectionId);

    function applyCandidates(candidates) {
      const freshGrid = document.getElementById('CartDrawerRecommendationsGrid');
      if (!freshGrid) return;

      let added = 0;
      for (let i = 0; i < candidates.length && added < remaining; i++) {
        const candidate = candidates[i];
        if (excludeIds.indexOf(candidate.dataset.productId) !== -1) continue;
        freshGrid.appendChild(candidate);
        added++;
      }
    }

    function attempt(url, allowFallback) {
      fetch(url)
        .then(function (response) {
          return response.text();
        })
        .then(function (text) {
          const html = document.createElement('div');
          html.innerHTML = text;
          const fillTarget = html.querySelector('#CartDrawerRecommendationsFillTarget');
          const candidates = fillTarget
            ? Array.prototype.slice.call(fillTarget.querySelectorAll('[data-product-id]'))
            : [];

          if (!candidates.length) {
            // intent=complementary is powered by real order history, which
            // a lower-volume/dev store may not have for this product yet --
            // Shopify returns performed=true, count=0 rather than an error
            // in that case. Fall back once to the default "related" intent
            // (tags/type/collection based, no order history required).
            if (allowFallback) attempt(baseUrl, false);
            return;
          }

          applyCandidates(candidates);
        })
        .catch(function (e) {
          console.error('[cart-recommendations] could not fetch top-up recommendations', e);
        });
    }

    attempt(baseUrl + '&intent=complementary', true);
  }

  // Coalesce bursts of mutations (a single innerHTML swap fires the
  // observer once per added/removed node, not once overall) into a single
  // fillRecommendations() call on the next tick.
  let scheduled = false;
  function scheduleFillRecommendations() {
    if (scheduled) return;
    scheduled = true;
    setTimeout(function () {
      scheduled = false;
      fillRecommendations();
    }, 0);
  }

  document.addEventListener('DOMContentLoaded', function () {
    fillRecommendations();

    subscribe(PUB_SUB_EVENTS.cartUpdate, function () {
      fillRecommendations();
    });

    // Primary signal: product-form.js publishes cartUpdate *before*
    // cart-drawer.js's renderContents() swaps #CartDrawer's innerHTML with
    // the freshly-rendered section (new fill-trigger data included) -- so
    // on "add to cart" the subscription above fires too early, against the
    // about-to-be-replaced DOM. Watching #CartDrawer itself for that swap
    // catches the moment the new markup actually lands, regardless of
    // which code path/timing triggered it.
    const cartDrawer = document.getElementById('CartDrawer');
    if (cartDrawer && window.MutationObserver) {
      new MutationObserver(scheduleFillRecommendations).observe(cartDrawer, {
        childList: true,
        subtree: true,
      });
    }
  });
})();
